import React, { useState, useEffect } from 'react';
import { 
    CreditCard, Download, Search, Filter, RefreshCw, 
    FileText, Mail, Printer, Undo, Eye, Clock, 
    Calendar, DollarSign, AlertCircle, CheckCircle,
    TrendingUp, PieChart, Send, Phone, Banknote
} from 'lucide-react';
import api from '../services/api';
import PaymentForm from '../components/Forms/PaymentForm';
import Modal from '../components/Common/Modal';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import Pagination from '../components/Common/Pagination';
import PaymentDetailsModal from '../components/PaymentDetailsModal';

const PAGE_SIZE = 20;

const Payments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [selectedPaymentForDetails, setSelectedPaymentForDetails] = useState(null);
    const [selectedLease, setSelectedLease] = useState(null);
    const [leases, setLeases] = useState([]);
    const [stats, setStats] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterMethod, setFilterMethod] = useState('');
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [refundReason, setRefundReason] = useState('');
    const [emailReceipt, setEmailReceipt] = useState('');
    const [sendingEmail, setSendingEmail] = useState(false);
    const [processingRefund, setProcessingRefund] = useState(false);

    useEffect(() => {
        fetchPayments();
        fetchLeases();
        fetchStats();
        setupAutoReminders();
    }, []);

    // Re-fetch when page changes
    useEffect(() => {
        fetchPayments();
    }, [page]);

    // Setup auto payment reminders
    const setupAutoReminders = async () => {
        try {
            await api.post('/payments/reminders/setup');
            console.log('Payment reminders setup');
        } catch (error) {
            console.error('Error setting up reminders:', error);
        }
    };

    const fetchPayments = async () => {
        try {
            setLoading(true);
            const response = await api.get(`/payments?page=${page}&limit=${PAGE_SIZE}`);
            let paymentsData = [];
            if (response.data.success && response.data.data) {
                paymentsData = response.data.data.data || response.data.data || [];
                const pagination = response.data.data.pagination;
                if (pagination) {
                    setTotalPages(pagination.pages || 1);
                    setTotalItems(pagination.total || 0);
                }
            }
            setPayments(paymentsData);
        } catch (error) {
            console.error('Error fetching payments:', error);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchLeases = async () => {
        try {
            const response = await api.get('/leases?status=active&limit=100');
            setLeases(response.data.data.data || []);
        } catch (error) {
            console.error('Error fetching leases:', error);
        }
    };


    const fetchStats = async () => {
        try {
            const response = await api.get('/payments/stats');
            setStats(response.data.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const handlePayment = async (data) => {
        try {
            console.log('Payment data received:', data);
            
            const paymentResponse = await api.post('/payments', {
                lease_id: data.leaseId,
                amount: parseFloat(data.amount),
                payment_type: data.paymentType || 'rent',
                payment_method: data.paymentMethod,
                payment_period_start: data.payment_period_start,
                payment_period_end: data.payment_period_end,
                metadata: { notes: data.notes }
            });
            
            const payment = paymentResponse.data.data;
            console.log('Payment created:', payment);
            
            if (data.paymentMethod === 'mpesa_stk' && data.phoneNumber) {
    await api.post(`/payments/${payment.id}/mpesa/initiate`, {
        phoneNumber: data.phoneNumber
    });
    alert('M-Pesa payment initiated. Please check your phone to complete payment.');
} else {
                alert('Payment recorded successfully!');
                if (data.paymentMethod === 'cash') {
                    // Optionally generate receipt
                    generateReceipt(payment.id);
                }
            }
            
            setShowModal(false);
            fetchPayments();
            fetchStats();
        } catch (error) {
            console.error('Error processing payment:', error);
            alert('Failed to process payment: ' + (error.response?.data?.error || error.message));
        }
    };

    const generateReceipt = async (paymentId) => {
        try {
            const response = await api.get(`/payments/${paymentId}/receipt`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `receipt_${paymentId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating receipt:', error);
            alert('Failed to generate receipt');
        }
    };

    const handleRefund = async () => {
        if (!refundReason) {
            alert('Please provide a reason for refund');
            return;
        }
        
        setProcessingRefund(true);
        try {
            await api.post(`/payments/${selectedPayment.id}/refund`, { reason: refundReason });
            alert('Payment refunded successfully!');
            setShowRefundModal(false);
            setRefundReason('');
            setSelectedPayment(null);
            fetchPayments();
            fetchStats();
        } catch (error) {
            console.error('Error processing refund:', error);
            alert('Failed to process refund: ' + (error.response?.data?.error || error.message));
        } finally {
            setProcessingRefund(false);
        }
    };

    const sendEmailReceipt = async () => {
        if (!emailReceipt) {
            alert('Please enter recipient email');
            return;
        }
        
        setSendingEmail(true);
        try {
            await api.post(`/payments/${selectedPayment.id}/email-receipt`, {
                email: emailReceipt
            });
            alert('Receipt sent successfully!');
            setShowReceiptModal(false);
            setEmailReceipt('');
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Failed to send email receipt');
        } finally {
            setSendingEmail(false);
        }
    };

    const handleRefresh = () => {
        fetchPayments();
        fetchStats();
    };
    
    const viewPaymentDetails = (payment) => {
        setSelectedPaymentForDetails(payment);
        setShowPaymentDetailsModal(true);
    };

    const onPageChange = (newPage) => {
        setPage(newPage);
    };

    const getStatusBadge = (status) => {
        const badges = {
            completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
            pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
            processing: { color: 'bg-blue-100 text-blue-800', icon: RefreshCw },
            failed: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
            refunded: { color: 'bg-gray-100 text-gray-800', icon: Undo }
        };
        const badge = badges[status] || badges.pending;
        const Icon = badge.icon;
        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                <Icon className="h-3 w-3 mr-1" />
                {status}
            </span>
        );
    };

    const getPaymentMethodIcon = (method) => {
        switch (method) {
            case 'cash': return <Banknote className="h-4 w-4 text-green-600" />;
            case 'mpesa_stk': return <Phone className="h-4 w-4 text-blue-600" />;
            case 'mpesa_paybill': return <Phone className="h-4 w-4 text-blue-600" />;
            case 'bank_transfer': return <CreditCard className="h-4 w-4 text-purple-600" />;
            default: return <CreditCard className="h-4 w-4 text-gray-600" />;
        }
    };

    // Client-side filter for search, status, method, date
    const filteredPayments = payments.filter(payment => {
        const matchesSearch = searchTerm === '' || 
            payment.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            payment.asset_name?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === '' || payment.status === filterStatus;
        const matchesMethod = filterMethod === '' || payment.payment_method === filterMethod;
        
        let matchesDate = true;
        if (dateRange.start && dateRange.end) {
            const paymentDate = new Date(payment.created_at);
            const startDate = new Date(dateRange.start);
            const endDate = new Date(dateRange.end);
            matchesDate = paymentDate >= startDate && paymentDate <= endDate;
        }
        
        return matchesSearch && matchesStatus && matchesMethod && matchesDate;
    });

    // Calculate late fees automatically
    const calculateLateFees = async () => {
        try {
            const response = await api.post('/payments/calculate-late-fees');
            alert(`Late fees calculated: ${response.data.message}`);
            fetchStats();
        } catch (error) {
            console.error('Error calculating late fees:', error);
        }
    };

    // Export payments to CSV
    const exportToCSV = () => {
        const headers = ['Date', 'Tenant', 'Email', 'Property', 'Unit', 'Amount', 'Method', 'Status', 'Receipt No.'];
        const csvData = filteredPayments.map(p => [
            new Date(p.created_at).toLocaleDateString(),
            `${p.first_name} ${p.last_name}`,
            p.email,
            p.asset_name,
            p.unit_number || '',
            p.amount,
            p.payment_method,
            p.status,
            p.mpesa_receipt_number || ''
        ]);
        
        const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `payments_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    if (loading) return <LoadingSpinner size="lg" />;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div className="shrink-0">
                    <h1 className="text-2xl font-bold text-gray-900">Payments</h1>
                    <p className="text-gray-600">Track and manage all payment transactions</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button
                        onClick={calculateLateFees}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                        title="Calculate late fees for overdue payments"
                    >
                        <AlertCircle className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Late Fees</span>
                        <span className="sm:hidden">Fees</span>
                    </button>
                    <button
                        onClick={exportToCSV}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                        title="Export CSV"
                    >
                        <FileText className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Export</span>
                        <span className="sm:hidden">CSV</span>
                    </button>
                    <button
                        onClick={handleRefresh}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                        title="Refresh"
                    >
                        <RefreshCw className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Refresh</span>
                        <span className="sm:hidden">Sync</span>
                    </button>
                    <button
                        onClick={() => setShowModal(true)}
                        className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                    >
                        <CreditCard className="h-4 w-4 sm:mr-1.5" />
                        <span className="hidden sm:inline">Make Payment</span>
                        <span className="sm:hidden">Pay</span>
                    </button>
                </div>
            </div>
            
            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Total Collected</p>
                        <p className="text-2xl font-bold text-green-600">KES {(stats.total_collected || 0).toLocaleString()}</p>
                        <TrendingUp className="h-4 w-4 text-green-500 mt-2" />
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Pending</p>
                        <p className="text-2xl font-bold text-yellow-600">KES {(stats.total_pending || 0).toLocaleString()}</p>
                        <Clock className="h-4 w-4 text-yellow-500 mt-2" />
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">This Month</p>
                        <p className="text-2xl font-bold text-indigo-600">KES {(stats.this_month_collected || 0).toLocaleString()}</p>
                        <Calendar className="h-4 w-4 text-indigo-500 mt-2" />
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Avg Payment</p>
                        <p className="text-2xl font-bold text-purple-600">KES {(stats.average_payment || 0).toLocaleString()}</p>
                        <DollarSign className="h-4 w-4 text-purple-500 mt-2" />
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <p className="text-sm text-gray-600">Overdue</p>
                        <p className="text-2xl font-bold text-red-600">KES {(stats.overdue_amount || 0).toLocaleString()}</p>
                        <AlertCircle className="h-4 w-4 text-red-500 mt-2" />
                    </div>
                </div>
            )}
            
            {/* Filters */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 border-b flex flex-col sm:flex-row sm:flex-wrap justify-between items-stretch sm:items-center gap-3">
                    <div className="flex items-center space-x-2 w-full sm:flex-1 sm:max-w-md">
                        <Search className="h-5 w-5 text-gray-400 shrink-0" />
                        <input
                            type="text"
                            placeholder="Search by tenant or property..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full border-none focus:ring-0 focus:outline-none"
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center space-x-1">
                            <Filter className="h-5 w-5 text-gray-400 shrink-0" />
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">All Status</option>
                                <option value="completed">Completed</option>
                                <option value="pending">Pending</option>
                                <option value="processing">Processing</option>
                                <option value="failed">Failed</option>
                                <option value="refunded">Refunded</option>
                            </select>
                        </div>
                        <div className="flex items-center space-x-1">
                            <CreditCard className="h-5 w-5 text-gray-400 shrink-0" />
                            <select
                                value={filterMethod}
                                onChange={(e) => setFilterMethod(e.target.value)}
                                className="border-gray-300 rounded-md text-sm focus:ring-indigo-500 focus:border-indigo-500"
                            >
                                <option value="">All Methods</option>
                                <option value="cash">Cash</option>
                                <option value="mpesa_stk">M-Pesa STK</option>
                                <option value="mpesa_paybill">M-Pesa Paybill</option>
                                <option value="bank_transfer">Bank Transfer</option>
                            </select>
                        </div>
                        <div className="flex items-center space-x-1">
                            <Calendar className="h-5 w-5 text-gray-400 shrink-0" />
                            <input
                                type="date"
                                value={dateRange.start}
                                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                                className="border-gray-300 rounded-md text-sm w-32 sm:w-auto"
                            />
                            <span className="text-gray-400">-</span>
                            <input
                                type="date"
                                value={dateRange.end}
                                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                                className="border-gray-300 rounded-md text-sm w-32 sm:w-auto"
                            />
                        </div>
                    </div>
                </div>
                
                {/* Payments Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Property</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredPayments.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                        No payments found
                                    </td>
                                </tr>
                            ) : (
                                filteredPayments.map((payment) => (
                                    <tr key={payment.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {new Date(payment.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <p className="text-sm font-medium text-gray-900">{payment.first_name} {payment.last_name}</p>
                                            <p className="text-sm text-gray-500">{payment.email}</p>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {payment.asset_name} {payment.unit_number ? `(Unit ${payment.unit_number})` : ''}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            KES {parseFloat(payment.amount || 0).toLocaleString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">
                                            <div className="flex items-center space-x-1">
                                                {getPaymentMethodIcon(payment.payment_method)}
                                                <span>{payment.payment_method?.replace('_', ' ') || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(payment.status)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex space-x-2">
                                                {payment.status === 'completed' && (
                                                    <>
                                                        <button
                                                            onClick={() => generateReceipt(payment.id)}
                                                            className="text-indigo-600 hover:text-indigo-800"
                                                            title="Download Receipt"
                                                        >
                                                            <Download className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={async () => {
                                                                try {
                                                                    await api.post(`/payments/${payment.id}/email-receipt`, {});
                                                                    alert('Receipt sent successfully to tenant\'s email');
                                                                } catch (error) {
                                                                    console.error('Error sending email:', error);
                                                                    alert('Failed to send receipt: ' + (error.response?.data?.error || error.message));
                                                                }
                                                            }}
                                                            className="text-blue-600 hover:text-blue-800"
                                                            title="Email Receipt"
                                                        >
                                                            <Mail className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedPayment(payment);
                                                                window.open(`/payments/${payment.id}/print`, '_blank');
                                                            }}
                                                            className="text-gray-600 hover:text-gray-800"
                                                            title="Print Receipt"
                                                        >
                                                            <Printer className="h-4 w-4" />
                                                        </button>
                                                    </>
                                                )}
                                                {payment.status === 'pending' && (
                                                    <button
                                                        onClick={() => {
                                                            setSelectedPayment(payment);
                                                            setShowRefundModal(true);
                                                        }}
                                                        className="text-red-600 hover:text-red-800"
                                                        title="Refund Payment"
                                                    >
                                                        <Undo className="h-4 w-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => viewPaymentDetails(payment)}
                                                    className="text-gray-600 hover:text-gray-800"
                                                    title="View Details"
                                                >
                                                    <Eye className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
                <Pagination
                    page={page}
                    totalPages={totalPages}
                    total={totalItems}
                    limit={PAGE_SIZE}
                    onPageChange={onPageChange}
                />
            )}

            {/* Payment Modal */}
            <Modal
                isOpen={showModal}
                onClose={() => {
                    setShowModal(false);
                    setSelectedLease(null);
                }}
                title="Make a Payment"
                size="lg"
            >
                <PaymentForm
                    onSubmit={handlePayment}
                    isLoading={false}
                    leases={leases}
                />
            </Modal>

            {/* Refund Modal */}
            <Modal
                isOpen={showRefundModal}
                onClose={() => {
                    setShowRefundModal(false);
                    setSelectedPayment(null);
                    setRefundReason('');
                }}
                title="Refund Payment"
                size="md"
            >
                <div className="space-y-4">
                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <p className="text-sm text-yellow-800">
                            You are about to refund payment of <strong>KES {selectedPayment?.amount?.toLocaleString()}</strong> 
                            from <strong>{selectedPayment?.first_name} {selectedPayment?.last_name}</strong>
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reason for Refund
                        </label>
                        <textarea
                            value={refundReason}
                            onChange={(e) => setRefundReason(e.target.value)}
                            rows={3}
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                            placeholder="Please provide a reason for this refund..."
                            required
                        />
                    </div>
                    <div className="flex justify-end space-x-3">
                        <button
                            onClick={() => {
                                setShowRefundModal(false);
                                setSelectedPayment(null);
                                setRefundReason('');
                            }}
                            className="px-4 py-2 border rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleRefund}
                            disabled={processingRefund}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                        >
                            {processingRefund ? 'Processing...' : 'Confirm Refund'}
                        </button>
                    </div>
                </div>
            </Modal>


            {/* Payment Details Modal */}
            <PaymentDetailsModal
                isOpen={showPaymentDetailsModal}
                onClose={() => {
                    setShowPaymentDetailsModal(false);
                    setSelectedPaymentForDetails(null);
                }}
                payment={selectedPaymentForDetails}
                onPaymentUpdate={() => {
                    fetchPayments();
                    fetchStats();
                }}
            />
        </div>
    );
};

export default Payments;
